import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { Logo } from "@/components/Logo";

const EFFECTIVE_DATE = "30 September 2026";
const SUPPORT_EMAIL = "admin@debiiapp.com";

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-20" data-testid={`policy-section-${id}`}>
      <h2 className="font-display text-lg md:text-xl font-bold text-foreground mt-8 mb-3">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-muted-foreground">{children}</div>
    </section>
  );
}

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-policy-page">
      <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur">
        <div className="max-w-2xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/" data-testid="policy-home-link" aria-label="Debii home">
            <Logo size={30} />
          </Link>
          <span className="text-xs font-semibold text-muted-foreground">Privacy Policy</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 pb-20">
        <div className="mt-8 rounded-2xl bg-primary text-primary-foreground p-6">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-300" />
            <p className="font-display font-bold text-lg">Privacy Policy</p>
          </div>
          <p className="mt-2 text-sm text-white/85 leading-relaxed">
            Debii is a personal debt-tracking tool that helps you privately record money you lend or borrow.
            We take the privacy of your records seriously.
          </p>
          <p className="mt-3 text-xs text-white/70">Effective date: {EFFECTIVE_DATE}</p>
        </div>

        <div className="mt-6 text-sm leading-relaxed text-muted-foreground">
          <p>
            This Privacy Policy explains how <strong className="text-foreground">Debii Technologies Limited</strong>{" "}
            (&ldquo;Debii&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;) collects, uses, stores
            and protects your information when you use the Debii application and website at{" "}
            <span className="text-foreground">debiiapp.com</span> (the &ldquo;Service&rdquo;). By using the
            Service, you agree to the practices described in this policy.
          </p>
        </div>

        <Section id="who-we-are" title="1. Who we are and what Debii is">
          <p>
            Debii is a personal debt-tracking platform. It helps you keep a private record of money you have lent
            to others or borrowed from them, send your own reminders, and optionally request that the other person
            acknowledges a record.
          </p>
          <p className="text-foreground font-medium">Debii is not a bank, lender or financial institution. Specifically, Debii does not:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>lend money or provide credit of any kind;</li>
            <li>collect debts on your behalf or act as a debt-collection agency;</li>
            <li>receive, hold, transfer or custody any customer funds;</li>
            <li>generate, publish or share public credit scores or borrower profiles.</li>
          </ul>
          <p>
            Where a repayment is made, it is made directly between you and the other person. Debii only helps you
            record and track it.
          </p>
        </Section>

        <Section id="information-we-collect" title="2. Information we collect">
          <p className="text-foreground font-medium">Account information</p>
          <p>
            When you create an account we collect your email address, a password (which we store only as a secure
            cryptographic hash, never in plain text), and your name or display name.
          </p>

          <p className="text-foreground font-medium">Debt records you enter</p>
          <p>
            Debii stores the records you choose to enter, including the names and (where you add them) phone numbers
            of the people you lend to or borrow from, amounts, currencies, dates, due dates, repayment notes and the
            history of payments you record. This information is entered by you and is used to provide the tracking
            features of the Service.
          </p>

          <p className="text-foreground font-medium">Repayment bank account details (optional)</p>
          <p>
            If you choose to use the repayment-account feature, you may provide a Nigerian bank account number and
            bank, which we verify through our payments partner (Paystack) and store so authorised recipients can
            transfer repayments to you directly. Account numbers are masked in the interface and only shown in full
            at the point where an authorised payer needs them to make a transfer.
          </p>

          <p className="text-foreground font-medium">Subscription and payment information</p>
          <p>
            If you subscribe to Debii Plus, payment is processed by Paystack. We store a record of your subscription
            status, renewal date and the payment references returned by Paystack. We do not collect or store your
            full card details — these are handled directly by Paystack.
          </p>

          <p className="text-foreground font-medium">Notifications and device tokens</p>
          <p>
            If you enable push notifications, we store a notification token issued by Firebase Cloud Messaging so we
            can deliver alerts about events on your account (such as acknowledgements, disputes and repayment
            updates). You can disable push notifications at any time.
          </p>

          <p className="text-foreground font-medium">Usage information</p>
          <p>
            We keep limited technical and activity records needed to operate the Service securely and reliably.
          </p>
        </Section>

        <Section id="how-we-use" title="3. How we use your information">
          <ul className="list-disc pl-5 space-y-1">
            <li>to provide, maintain and improve the debt-tracking features you use;</li>
            <li>to authenticate you and keep your account secure;</li>
            <li>to send transactional emails such as email verification, password resets and account notices;</li>
            <li>to deliver reminders and acknowledgement/repayment links that you choose to send;</li>
            <li>to process and manage your Debii Plus subscription;</li>
            <li>to deliver push notifications you have enabled;</li>
            <li>to detect, prevent and respond to fraud, abuse or security issues;</li>
            <li>to comply with our legal and regulatory obligations.</li>
          </ul>
          <p>We do not sell your personal data, and we do not use it to build public credit scores or borrower profiles.</p>
        </Section>

        <Section id="acknowledgement-links" title="4. Acknowledgement, reminder and repayment links">
          <p>
            Debii lets you generate secure links to share a specific record with the other person, so they can
            acknowledge it, dispute it, view it or report a repayment — without needing a Debii account.
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Each link uses a long, randomly generated, non-guessable token.</li>
            <li>A link reveals only the single record you chose to share — never your other records or your account.</li>
            <li>Bank repayment details are only shown through a link after the appropriate step is reached, and are never included in the text of a WhatsApp or copied reminder message.</li>
            <li>You choose when and to whom a link is sent. Debii never messages anyone on your behalf.</li>
          </ul>
        </Section>

        <Section id="third-parties" title="5. Third-party services we use">
          <p>We rely on a small number of trusted service providers to operate the Service. Each processes only the data needed for its function:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong className="text-foreground">Paystack</strong> — processes Debii Plus subscription payments and verifies Nigerian bank account details for the repayment-account feature.</li>
            <li><strong className="text-foreground">Resend</strong> — delivers transactional emails such as verification, password-reset and account notices.</li>
            <li><strong className="text-foreground">Firebase Cloud Messaging (Google)</strong> — delivers push notifications to users who enable them.</li>
          </ul>
          <p>
            These providers process your information under their own privacy policies and applicable data-protection
            agreements. We share with them only what is necessary to provide the relevant feature.
          </p>
        </Section>

        <Section id="security" title="6. How we protect your information">
          <p>
            We use reasonable technical and organisational measures to protect your information, including encrypted
            connections (HTTPS), hashed password storage, authentication tokens stored in secure cookies, and
            access controls that keep your records private to your account.
          </p>
          <p>
            No method of transmission or storage is completely secure, and we cannot guarantee absolute security.
            We encourage you to use a strong, unique password and to keep your login credentials confidential.
          </p>
        </Section>

        <Section id="retention" title="7. Data retention">
          <p>
            We retain your information for as long as your account is active and for as long as needed to provide the
            Service. When you delete your account, we remove your account and its associated records from our active
            systems.
          </p>
          <p>
            Some information may be retained for a limited additional period where we are required or permitted to do
            so — for example, transaction and subscription records that we must keep to meet legal, accounting,
            tax or regulatory obligations, or to resolve disputes and enforce our terms. Residual copies may also
            persist briefly in routine backups before being overwritten.
          </p>
        </Section>

        <Section id="your-rights" title="8. Your privacy rights">
          <p>Subject to applicable law, including the Nigeria Data Protection Act 2023 and the NDPR, you have the right to:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>access the personal information we hold about you;</li>
            <li>correct inaccurate or incomplete information;</li>
            <li>delete your account and associated records (see &ldquo;Account deletion&rdquo; below);</li>
            <li>object to or restrict certain processing;</li>
            <li>withdraw consent, such as by disabling push notifications;</li>
            <li>lodge a complaint with the Nigeria Data Protection Commission (NDPC).</li>
          </ul>
          <p>To exercise any of these rights, contact us using the details below.</p>
        </Section>

        <Section id="account-deletion" title="9. Account deletion">
          <p>
            You can delete your account at any time from{" "}
            <span className="text-foreground">Profile → Privacy → Delete Account</span> (or Account settings) within
            the app. Deleting your account removes your debts, people, payments, activity, notifications,
            acknowledgements, subscription records, notification tokens and your user profile from our active systems.
          </p>
          <p>
            As noted in &ldquo;Data retention&rdquo; above, certain records may be retained for a limited period where
            we are legally required to keep them. If you need help deleting your account or your data, contact us at{" "}
            <a className="text-primary font-medium" href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </Section>

        <Section id="children" title="10. Children's privacy">
          <p>
            Debii is not intended for children under 18. We do not knowingly collect personal information from
            children. If you believe a child has provided us with personal information, please contact us so we can
            remove it.
          </p>
        </Section>

        <Section id="changes" title="11. Changes to this policy">
          <p>
            We may update this Privacy Policy from time to time. When we make material changes, we will update the
            effective date at the top of this page and, where appropriate, notify you within the app.
          </p>
        </Section>

        <Section id="contact" title="12. Contact us">
          <p>
            Debii is operated by <strong className="text-foreground">Debii Technologies Limited</strong>. If you have
            any questions, requests or complaints about this policy or your data, contact us at:
          </p>
          <p>
            <a className="text-primary font-medium" href={`mailto:${SUPPORT_EMAIL}`} data-testid="policy-contact-email">
              {SUPPORT_EMAIL}
            </a>
          </p>
        </Section>

        <p className="mt-10 text-xs text-muted-foreground">Last updated: {EFFECTIVE_DATE}</p>
      </main>
    </div>
  );
}
